import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsPatientDocuments']
const INTAKE_FORM_KEY = ['SettingsIntakeForm']
const INTAKE_FORM_PREVIEW_KEY = ['SettingsIntakeFormPreview']

export const useSettingsPatientDocuments = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/settings/patient_documents`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the patient documents")
      }
      return res.json()
    },
  })

  return { documents: data, isLoading, isError }
}

export const useIntakeForm = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: INTAKE_FORM_KEY,
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/patient_documents/intake_form`,
        {
          method: 'GET',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the intake form")
      }
      return res.json()
    },
    enabled: false,
  })

  return { intakeForm: data, isLoading, isError, refetch }
}

export const useIntakeFormPreview = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: INTAKE_FORM_PREVIEW_KEY,
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/patient_documents/intake_form/preview`,
        {
          method: 'GET',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the intake form preview")
      }
      return res.json()
    },
    enabled: false,
  })

  return { previewData: data, isLoading, isError, refetch }
}

export const useUpdateIntakeForm = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateIntakeForm, ...rest } = useMutation({
    mutationFn: async formData => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/patient_documents/intake_form`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ intake_form_setting: formData }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update intake form')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
      queryClient.invalidateQueries(INTAKE_FORM_KEY)
    },
  })

  return { updateIntakeForm, ...rest }
}
